import { useQuery } from "@tanstack/react-query";
import type { OpsCustomerSummary } from "../../shared/operations-schemas";
import { documentMoney } from "../knowledge/customer-document-format";
import { billingQueryKey, getBilling } from "./billing-api";
import { agreedLine, collectedMinor, pastDueLine } from "./customer-page-format";

/** Three numbers first: what was agreed, what came in, and what is late (ADR-0088). */
export function CustomerBillingSummary({ customer }: { customer: OpsCustomerSummary }) {
  const query = useQuery({
    queryKey: billingQueryKey(customer.slug),
    queryFn: () => getBilling(customer.slug),
  });
  const billing = query.data?.billing;
  if (!billing) return null;
  const totals = [
    {
      label: "Agreed",
      value: agreedLine(billing),
      detail: billing.terms.some((terms) => terms.status === "approved")
        ? "From approved terms"
        : "A quote the customer accepts sets this",
    },
    {
      label: "Collected",
      value: documentMoney(collectedMinor(billing)),
      detail: billing.invoices.length ? "Paid, less refunds" : "Nothing invoiced yet",
    },
    {
      label: "Past due",
      value: pastDueLine(billing),
      detail: billing.outstandingMinor
        ? `${documentMoney(billing.outstandingMinor)} outstanding`
        : "Nothing outstanding",
    },
  ];
  return (
    <dl className="m-0 grid gap-4 md:grid-cols-3">
      {totals.map((total) => (
        <div key={total.label} className="rounded-xl bg-card p-6 shadow-card">
          <dt className="text-sm font-semibold text-muted-foreground">{total.label}</dt>
          <dd className="m-0 mt-1 text-2xl leading-tight font-bold tabular-nums">{total.value}</dd>
          <dd className="m-0 mt-1 text-muted-foreground">{total.detail}</dd>
        </div>
      ))}
    </dl>
  );
}
